import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ArrowRight, Check, AlertTriangle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const ZONES = [
  { num: 1, name: "Foundation", color: "#E24B4A" },
  { num: 2, name: "Building", color: "#EF9F27" },
  { num: 3, name: "Practising", color: "#CBCB2A" },
  { num: 4, name: "Mastering", color: "#639922" },
  { num: 5, name: "Pioneering", color: "#C9A84C" },
];

const DIM_COLORS = {
  "Think Power": "#14B8A6",
  "Do Power": "#8A9A5B",
  "Connect Power": "#5582B7",
  "Adapt Power": "#9333EA",
};

export default function CapScanRatingPage() {
  const { user, refreshUser } = useAuth();
  const { scan_id } = useParams();
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [employeeIdx, setEmployeeIdx] = useState(0);
  const [ratings, setRatings] = useState({});  // { empIdx: { skillKey: {current, required, notes} } }
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) navigate("/login");
    if (scan_id && user) {
      axios.get(`${API}/api/capscan/${scan_id}`, { withCredentials: true })
        .then((r) => {
          setScan(r.data);
          // Initialize ratings with defaults
          const initial = {};
          (r.data.employees || []).forEach((_, i) => {
            initial[i] = {};
            (r.data.skills || []).forEach((sk, j) => {
              const existing = r.data.ratings_by_employee?.[String(i)]?.[j];
              initial[i][j] = existing || { current_zone: null, required_zone: r.data.default_required_zone || 3, notes: "" };
            });
          });
          setRatings(initial);
        }).catch(() => setError("Failed to load scan"));
    }
  }, [scan_id, user, navigate]);

  if (!scan) return <div className="min-h-screen bg-cream flex items-center justify-center pt-16"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  const skills = scan.skills || [];
  const empCount = scan.employees.length;
  const currentEmp = scan.employees[employeeIdx];
  const currentRatings = ratings[employeeIdx] || {};
  const ratedCount = Object.values(currentRatings).filter((r) => r.current_zone != null).length;
  const totalRated = Object.values(ratings).reduce((sum, empR) => sum + Object.values(empR).filter((r) => r.current_zone != null).length, 0);
  const totalNeeded = empCount * skills.length;
  const allRated = totalRated === totalNeeded;

  // Distribution warning
  const zoneCounts = {};
  Object.values(currentRatings).forEach((r) => { if (r.current_zone) zoneCounts[r.current_zone] = (zoneCounts[r.current_zone] || 0) + 1; });
  const overloadedZone = Object.entries(zoneCounts).find(([z, c]) => c >= 8);

  const setRating = (skillIdx, field, val) => {
    const copy = { ...ratings };
    copy[employeeIdx] = { ...(copy[employeeIdx] || {}) };
    copy[employeeIdx][skillIdx] = { ...(copy[employeeIdx][skillIdx] || {}), [field]: val };
    setRatings(copy);
    // Auto-save (debounced via timeout)
    saveRatings(employeeIdx, copy[employeeIdx]);
  };

  let saveTimer;
  const saveRatings = (idx, empRatings) => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(async () => {
      const arr = skills.map((sk, j) => ({
        skill: sk.text,
        dimension: sk.dimension || "Do Power",
        current_zone: empRatings[j]?.current_zone || 1,
        required_zone: empRatings[j]?.required_zone || 3,
        notes: empRatings[j]?.notes || "",
      })).filter((_, j) => empRatings[j]?.current_zone != null);
      if (arr.length === 0) return;
      try {
        await axios.post(`${API}/api/capscan/save-ratings`, { scan_id, employee_index: idx, ratings: arr }, { withCredentials: true });
      } catch { /* silent */ }
    }, 800);
  };

  const handleGenerate = async () => {
    setError("");
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/capscan/${scan_id}/generate`, {}, { withCredentials: true });
      await refreshUser();
      navigate(`/capscan/report/${res.data.report_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const tokenCost = scan.scan_type === "individual" ? 2 : empCount <= 10 ? 5 : 8;

  return (
    <div className="min-h-screen bg-cream pt-20 pb-32">
      <div className="bg-white border-b border-navy/10 sticky top-16 z-30 px-6 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <Link to="/capscan/new" className="text-navy/60 hover:text-gold transition-colors text-sm font-dm flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Setup
          </Link>
          <div className="flex-1 max-w-md">
            <div className="flex items-center justify-between mb-1">
              <p className="font-dm text-navy/60 text-xs">Rating {ratedCount} of {skills.length} skills{empCount > 1 ? ` · ${currentEmp.name}` : ""}</p>
              <p className="font-dm text-gold text-xs font-bold">{Math.round((ratedCount / Math.max(skills.length, 1)) * 100)}%</p>
            </div>
            <div className="h-2 bg-navy/10 rounded-full overflow-hidden">
              <div className="h-full bg-gold transition-all" style={{ width: `${(ratedCount / Math.max(skills.length, 1)) * 100}%` }} />
            </div>
          </div>
          {empCount > 1 && (
            <select value={employeeIdx} onChange={(e) => setEmployeeIdx(Number(e.target.value))} className="border border-navy/20 rounded-lg px-3 py-1.5 font-dm text-xs">
              {scan.employees.map((e, i) => <option key={i} value={i}>{e.name || `Person ${i + 1}`}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-6 space-y-4">
        {overloadedZone && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="font-dm text-navy text-sm">You have rated <strong>{overloadedZone[1]}</strong> skills at <strong>{ZONES[overloadedZone[0] - 1].name}</strong>. Does this accurately reflect real performance differences between skills?</p>
          </div>
        )}

        {skills.map((sk, idx) => {
          const r = currentRatings[idx] || {};
          const dim = sk.dimension || "Do Power";
          return (
            <div key={idx} className="bg-white rounded-xl shadow-md p-6" data-testid={`skill-card-${idx}`}>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <span className="inline-block text-white font-dm text-[10px] font-bold uppercase px-2.5 py-1 rounded-full mb-2" style={{ backgroundColor: DIM_COLORS[dim] || "#888" }}>{dim}</span>
                  <h3 className="font-playfair text-navy text-lg font-bold leading-snug">{sk.text}</h3>
                </div>
                {r.current_zone && <Check className="w-5 h-5 text-green-500 flex-shrink-0" />}
              </div>
              <p className="font-dm text-navy/60 text-xs mb-2 font-semibold">Current zone:</p>
              <div className="grid grid-cols-5 gap-2 mb-4">
                {ZONES.map((z) => (
                  <button key={z.num} onClick={() => setRating(idx, "current_zone", z.num)} data-testid={`current-zone-${idx}-${z.num}`}
                    className={`px-2 py-2.5 rounded-lg font-dm text-xs font-bold transition-all border-2 ${r.current_zone === z.num ? "text-white" : "bg-white"}`}
                    style={{
                      borderColor: z.color,
                      backgroundColor: r.current_zone === z.num ? z.color : "white",
                      color: r.current_zone === z.num ? "white" : z.color,
                    }}>
                    {z.name}
                  </button>
                ))}
              </div>
              <p className="font-dm text-navy/60 text-xs mb-2 font-semibold">Zone required for role:</p>
              <div className="grid grid-cols-5 gap-2 mb-3">
                {ZONES.map((z) => (
                  <button key={z.num} onClick={() => setRating(idx, "required_zone", z.num)} data-testid={`required-zone-${idx}-${z.num}`}
                    className={`px-2 py-1.5 rounded-lg font-dm text-[10px] font-bold transition-all border ${r.required_zone === z.num ? "border-navy bg-navy text-gold" : "border-navy/20 text-navy/50 hover:border-navy/40"}`}>
                    {z.name}
                  </button>
                ))}
              </div>
              <textarea value={r.notes || ""} onChange={(e) => setRating(idx, "notes", e.target.value)} placeholder="Observations or evidence for this rating..." rows={2}
                className="w-full border border-navy/10 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none resize-none" />
            </div>
          );
        })}

        {error && <p className="font-dm text-red-600 text-center text-sm">{error}</p>}

        <div className="bg-navy rounded-xl p-6 text-center sticky bottom-4 shadow-xl">
          <p className="font-dm text-white/70 text-sm mb-1">{allRated ? "All ratings complete." : `${totalNeeded - totalRated} skill ratings remaining.`}</p>
          <p className="font-dm text-gold text-xs mb-4">This will use <strong>{tokenCost} intelligence tokens</strong> from your balance ({user?.tokens ?? 0}).</p>
          <button onClick={handleGenerate} disabled={!allRated || generating || (user?.tokens ?? 0) < tokenCost} data-testid="generate-report-btn"
            className="bg-gold text-navy font-dm font-bold px-10 py-3 rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50">
            {generating ? "Generating report..." : "Generate Report"}
          </button>
        </div>
      </div>
    </div>
  );
}
